/**
 * ⚠️ **Important Notice** ⚠️
 *
 * **Synchronization Required**: When making any modifications to the structure of the
 * `ACCESS_TO_THE_COMPONENT` object or the `AccessKey` enum, it is crucial to synchronize
 * these changes with the backend. This is essential to ensure that the frontend and backend
 * data structures remain consistent and function correctly.
 *
 * **Action Required**:
 * - Communicate any updates to the backend development team.
 * - Ensure that the corresponding API or data models are updated to reflect the new structure.
 *
 * Keeping both frontend and backend in sync will help avoid potential issues and maintain
 * smooth functionality across the application.
 */
enum AccessKey {
	SHOW = "show",
	MAIN = "main",
	LOGIN = "login",
	ORDERS = "orders",
	ACCOUNTING = "accounting",
	ACCOUNTS = "accounts",
	ARCHIVES = "archives",
	RATINGS = "ratings",
	RATINGS_EXECUTORS = "ratingsExecutors",
	REPORTS = "reports",
	REPORTS_CHARTS = "reportsCharts",
	REPORTS_ACCOUNTING = "reportsAccounting",
	REPORTS_SALARY = "reportsSalary",
	CUSTOMERS = "customers",
	BLACKLIST = "blacklist",
	PREFERENCES_PAGE_NAME = "preferencesPageName",
	SETTINGS = "settings",
	SETTINGS_ORDERS = "settingsOrders",
	SETTINGS_TARIFFS = "settingsTariffs",
	SETTINGS_FINANCES = "settingsFinances",
	SETTINGS_MESSAGES = "settingsMessages",
	SETTINGS_MOBILE_APPS = "settingsMobileApps",
	SETTINGS_PARAMETERS = "settingsParameters",
	SETTINGS_MAP = "settingsMap",
	SETTINGS_ORDER_EXCHANGE = "settingsOrderExchange",
	SETTINGS_PAYMENT_SYSTEMS = "settingsPaymentSystems",
	SETTINGS_RECEIPT_PROVIDERS = "settingsReceiptProviders",
	SETTINGS_INTERFACE = "settingsInterface",
	SETTINGS_VOIP = "settingsVoip",
	SETTINGS_CAR_PARKS = "settingsCarParks",
	SETTINGS_SYSTEM_PERFORMANCE = "settingsSystemPerformance",
	SYSTEM_PERFORMANCE_OVERVIEW = "systemPerformanceOverview",
	SYSTEM_PERFORMANCE_INCIDENTS = "systemPerformanceIncidents",
	SYSTEM_PERFORMANCE_OPERATIONS = "systemPerformanceOperations",
	SYSTEM_PERFORMANCE_CLIENTS = "systemPerformanceClients",
	EXECUTORS = "executors",
	TRANSPORT = "transport",
	USERS = "users",
	ROLES = "roles",
	CAR_PARKS = "carParks",
	SCHEDULES = "schedules",
	EXECUTOR_TEAMS = "executorTeams",

	CLIENTS = "clients",
	COUNTERPARTIES = "counterparties",
	AGENTS = "agents",
	CLIENT_GROUPS = "clientGroups",
	COUNTERPARTY_GROUPS = "counterpartyGroups",
	AGENT_GROUPS = "agentGroups",

	BLACKLIST_CUSTOMERS = "blacklistCustomers",
	BLACKLIST_EXECUTORS = "blacklistExecutors",
	BLACKLIST_TRANSPORT = "blacklistTransport",

	BANK_TRANSACTIONS = "bankTransactions",
	TRANSACTIONS = "transactions",
	RATING_CHANGES = "ratingChanges",

	GPS_LOG = "gpsLog",
	MESSAGES = "messages",
	PUSH = "push",

	CALL_HISTORY = "callsHistory",
	RECEIPTS_HISTORY = "receiptsHistory",

	EXECUTORS_GENERAL = "executorsGeneral",
	BANK_ACCOUNTS = "bankAccounts",
	REPLENISH_PERFORMERS_BALANCE = "replenishPerformersBalance",

	GENERAL = "general",

	CASHLESS_ORDERS = "cashlessOrders",
	CASHLESS_ORDERS_GENERAL = "cashlessOrdersGeneral",
	CASHLESS_ORDERS_PERIOD_REPORT = "cashlessOrdersPeriodReport",
	CASHLESS_ACCOUNTS_SUMMARY_REPORT = "cashlessAccountsSummaryReport",
	FUEL_DEDUCTION_ORDERS_REPORT = "fuelDeductionOrdersReport",
	AGENT_ORDERS_REPORT = "agentOrdersReport",
	ACCOUNTS_REPORT = "accountsReport",

	USERS_ACRIVITY = "usersActivity",

	ORDERS_DISTRIBUTION = "ordersDistribution",
	FEED_TIME_CALCULATION = "feedTimeCalculation",

	MAIN_TARIFFS = "mainTariffs",
	AUTOMATIC_TARIFFS = "automaticTariffs",
	GENERAL_TARIFFS = "generalTariffs",
	ADDITIONAL = "additional",

	EXECUTOR_TARIFF_PLANS = "executorTariffPlans",
	CUSTOMER_BALANCES = "customerBalances",
	CUSTOMER_TARIFF_PLANS = "customerTariffPlans",
	USER_TARIFF_PLANS = "userTariffPlans",
	EXECUTOR_PENALTIES = "executorPenalties",
	EXECUTOR_RATINGS = "executorRatings",
	CUSTOMER_PENALTIES = "customerPenalties",
	USER_PENALTIES = "userPenalties",

	PROVIDER_SMS = "providerSms",
	TEMPLATES_SMS = "templatesSms",
	PUSH_NOTICES = "pushNotices",

	MOBILE_APP_EXECUTOR = "mobileAppExecutor",
	MOBILE_APP_CLIENT = "mobileAppClient",

	APPLICATION = "application",

	CUSTOMER = "customer",
	EXECUTOR = "executor",
	TERMINAL_EXECUTOR = "terminalExecutor",

	COLORS = "colors",
	FONT = "font",
	KEYBINDS = "keybinds",

	CAR_MODELS_AND_BRANDS = "settingsCarModelsAndBrands",
	CAR_CLASSES = "carClasses",
	OBJECTS = "objects",
	SECTORS = "sectors",
	PARKINGS = "parkings",
	PRICE_ZONES = "priceZones",
	SERVICES = "services",

	CONTROL_BUTTON_ADD = "controlButtonAdd",
	CONTROL_BUTTON_EDIT = "controlButtonEdit",
	CONTROL_BUTTON_DELETE = "controlButtonDelete",

	NEW_ORDER = "newOrder",
	EDIT_ORDER = "editOrder",
	REVERT_ORDER = "revertOrder",
	CLOSED_ORDER = "closeOrder",
	COPY_ORDER = "copyOrder",
	SET_ORDER_EXECUTOR = "setOrderExecutor",
	APPROVE_ORDER = "approveOrder",
	SEND_ORDER_AGAIN = "sendOrderAgain",
	RECALCULATE_ORDER = "recalculateOrder",
	RECALCULATE_ORDER_WITH_ACTUAL_RATES = "recalculateOrderWithActualRates",
	RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR = "returnOrderOnAirOrRemoveExecutor",
	SEND_ORDER_TO_AIR = "sendOrderToAir",
	SEND_ORDER_TO_EXECUTING = "sendOrderToExecuting",
	TOGGLE_ORDER_AIR = "toggleOrderAir",
	SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED = "setOrderCallToClientBeingRequired",
	SET_ORDER_CALL_TO_CLIENT_SUCCESS = "setOrderCallToClientSuccess",
	SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED = "setOrderCallToExecutorBeingRequired",
	SET_ORDER_CALL_TO_EXECUTOR_SUCCESS = "setOrderCallToExecutorSuccess",
	SHOW_CLIENT_NUMBER_TO_EXECUTOR = "showClientNumberToExecutor",
	COLOR_ORDER = "colorOrder",
	CALL_ORDER_CLIENT = "callOrderClient",
	CALL_ORDER_EXECUTOR = "callOrderExecutor",
	SMS_BUTTON = "SMSButton",
	AUTO_CALL_CLIENT_BUTTON = "AutoCallClientButton",
	ORDER_RETURN_FROM_TRANSFER = "orderReturnFromTransfer",
	ORDER_SEND_TO_TRANSFER = "orderSendToTransfer",
	ORDER_SEND_TO_TRANSFER_LOCAL = "orderSendToTransferLocal",
	ORDER_SEND_TO_TRANSFER_GLOBAL = "orderSendToTransferGlobal",
	ORDER_SEND_TO_TRANSFER_OUTSIDE = "orderSendToTransferOutside",

	WIDGET_ORDER_PARKING = "widgetOrderParkings",
	WIDGET_ORDER_EXECUTORS = "widgetOrderExecutors",
	WIDGET_ORDER_MAP = "widgetOrderMap",
	WIDGET_ORDER_ORDER_DETAILS = "widgetOrderOrderDetails",
	WIDGET_ORDER_CHAT = "widgetOrderChat",

	CALL = "call",
	SENDING_SMS = "sendingSMS",
	SENDEING_PUSH = "sendingPush",
	BALANSE = "balance",
	BONUS_BALANSE = "bonusBalance",
	IMPORT = "import",
	EXPORT = "export",
	REVERT_ORDER_TO_ARCHIVE = "revertOrderTOArchive",
	FILTER = "filter",

	REFRESH = "refresh",
	BALANCE_CHANGE = "balanceChange",

	LIVE_ORDERS = "liveOrders",
	PRELIMINARY_ORDERS = "preliminaryOrders",
	EXECUTING_ORDERS = "executingOrders",
	ALL_ORDERS = "allOrders",
	CLOSED_ORDERS = "closedOrders",

	AFTER_ADDITIONAL_BUTTONS = "afterAdditionalButtons",
	METADATA = "metadata",
	MENU = "menu",
	PAGES = "pages",
	TABS = "tabs",
	BUTTONS = "buttons",
	WIDGETS = "widgets",
	MODALS = "modals",
	FIELDS = "fields",
	CHECK_BOXS = "checkBoxs",
	CARD_ORDERS = "cardOrders",
	SELECTOR = "selector",
	EDITORS = "editors",
	RADIO_BUTTONS = "radioButtons",
	PERMISSION = "permission",
	PERFORMER_CARD = "performerCard",
	TRANSPORT_CARD = "transportCard",
	BLOCKS = "blocks",

	SEARCH_CAR = "searchCar",
	SEARCH_EXECUTOR = "searchExecutor",
	SEARCH_ORDER = "searchOrder",

	ADD_POINT = "addPoint",
	INSERT_POINT = "insertPoint",
	EDIT_POINT = "editPoint",
	DELETE_POINT = "deletePoint",
	MOVE_POINT_UP = "movePointUp",
	MOVE_POINT_DOWN = "movePointDown",

	ORDER_MODAL = "orderModal",

	SHOW_ACTIVE_CLIENT = "showActiveClient",
	SHOW_NO_ACTIVE_CLIENT = "showNoActiveClient",
	SHOW_ACTIVE_COUNTERPARTIES = "showActiveCounterparties",
	SHOW_NO_ACTIVE_COUNTERPARTIES = "showNoActiveCounterparties",

	ARCHIVE_FILTER_EXECUTOR = "archiveFilterExecutor",
	ARCHIVE_FILTER_STATUS_ORDER = "archiveFilterStatusOrder",
	ARCHIVE_FILTER_NUMBER_ORDER = "archiveFilterNumberOrder",
	ARCHIVE_FILTER_NUMBER_PHONE = "archiveFilterNumberPhone",
	ARCHIVE_FILTER_STREET = "archiveFilterStreet",
	ARCHIVE_FILTER_CLIENT = "archiveFilterClient",
	ARCHIVE_FILTER_COUNTERPARTY = "archiveFilterCounterparty",
	ARCHIVE_FILTER_AGENT = "archiveFilterAgent",
	ARCHIVE_FILTER_PAYMENTS = "archiveFilterPayments",
	ARCHIVE_FILTER_CLASSES = "archiveFilterClasses",
	ARCHIVE_FILTER_SERVICES = "archiveFilterServices",
	ARCHIVE_FILTER_SECTORS = "archiveFilterSectors",
	ARCHIVE_FILTER_TRANSFER = "archiveFilterTransfer",
	ARCHIVE_FILTER_SOURCE_CALL_SIGN = "archiveFilterSourceCallSign",

	SHOW_ONLY_MY_ORDERS = "showOnlyMyOrders",
	SHOW_ORDERS_WITHOUT_EXECUTORS = "showOrdersWithoutExecutors",
	FILTER_ORDERS_BY_MULTY = "filterOrdersByMulty",
	GPS_SPOOFING = "gpsSpoofing",
	PAYMENT_LINK_SMS = "paymentLinkSms",

	RELEVANCE = "relevance",
	CHOISE_CUSTOMERS = "choiseCustomers",
	SEND_SMS = "sendSMS",

	CHANGE_PERFORMERS_STATUS = "changeOfPerformerStatus",
	CHANGE_PERFORMERS_STATUS_SELECTED = "changeOfPerformerStatusSelected",
	SEARCH = "search",
	LOWERS_STATISTICS_TABLE = "lowerStatisticsTable",

	MAP_FILTER_POINTS = "mapFilterPoints",
	MAP_FILTER_PRICE_ZONE = "mapFilterPriceZone",
	MAP_FILTER_SECTOR = "mapFilterSector",
	MAP_FILTER_PARKING = "mapFilterParking",
	MAP_FILTER_EXECUTOR = "mapFilterExecutor",
	MAP_FILTER_CLIENT = "mapFilterClient",

	REPLY_TO_A_MESSAGE = "replyToAMessage",
	COMPANIES_AND_TAXI_SERVICES = "companiesAndTaxiServices",
	STATUS = "status",
	IS_WORKING = "isWorking",
	BY_ORDER = "byOrder",
	PARKING_GROUP_LIST = "parkingGroupList",

	OUT_OF_TOWN_DISTANCE = "outOfTownDistance",
	IDLE_TIME = "idleTime",
	PRICE = "price",
	ADDITIONAL_PRICE = "additionalPrice",
	AWAITING_TIME = "awaitingTime",
	HOURLY_TIME = "hourlyTime",

	PHONE = "phone",
	FROM = "from",
	TO = "to",

	FLEET_NUMBER = "fleetNumber",
	LINCENSE_PLATE = "licensePlate",

	MAP = "map",
	EVENTS = "events",
	CONVERSATIONS = "conversations",

	ROUTE_MAP = "routeMap",
	PRICE_ZONES_MAP = "priceZonesMap",
	SECTORS_MAP = "sectorsMap",
	PARKINGS_MAP = "parkingsMap",
	DRIVER_MAP = "driverMap",
	CLIENT_MAP = "clientMap",
	CENTER_MAP = "centerMap",
	TRACK_MAP = "trackMap",

	STREET = "street",
	LOCAL_OBJECT = "localObject",
	OBJECT = "object",

	CLOSE_ALL = "closeAll",
	CANCEL = "cancel",
	SAVE = "save",

	AUTO_SUPPLY = "autoSupply",
	OUT_OF_TOWN = "outOfTown",
	SUPPLY = "supply",
	HOURLY = "hourly",
	TAXIMETER = "taximeter",

	DISCOUNT_PLAN = "discountPlan",
	BONUS_TRIP = "bonusTrip",
	BONUS_BALANCE = "bonusBalance2",
	ACCUMULATIVE_DISCOUNT = "accumulativeDiscount",
	MAX_BONUSES_AND_INPUT = "maxBonusesAndInput",

	ORDER_TIME = "orderTime",
	PAYMENT_TYPE = "paymentType",
	TARIFF = "tariff",
	PASSENGER_COUNT = "passengerCount",

	PERSONAL_INFORMATION = "personalInformation",
	POINT_TABLE = "pointsTable",

	TRANSPORTATION_TYPE = "transportationType",
	ADD_NEW_ORDER = "addNewOrder",

	VIEW = "view",
	EDIT = "edit",

	ORDER_COUNT = "orderCount",
	ONLY_MOBILE_APP = "onlyMobileApp",

	CAR_OPTIONS = "carOptions",
	FAVORITES_AND_NON_FAVORITES = "favoritesAndNonFavorites",
	ACTIVE_AND_INACTIVE = "activeAndInactive",
	BASE_AND_CREATED = "baseAndCreated",

	TABLE_NAVIGATION = "tableNavigation",
	LANGUAGE = "language",

	OBJECTS_AND_OBJECT_GROUPS = "objectsAndObjectGroups",
	SHOW_ALL = "showAll",
	SHOW_OBJECTS = "showObjects",
	SHOW_STREETS = "showStreets",
	HIDDEN_IN_CLIENT_APP = "hiddenInClientApp",
	HIDE_INACTIVE = "hideInactive",

	FORM_REPORT_AND_FILTER = "formReportAndFilter",
	GROUP_ACTION = "groupAction",
	SAVE_AS_FILE = "saveAsFile",
	PRINT = "print",

	DATE = "date",
	GENERATE = "generate",
	CHART_TYPE = "chartType",
	TIME_TYPE = "timeType",
	ADD_ONLY_IF_OPERATIONS_IN_PERIOD = "addOnlyIfOperationsInPeriod",

	ONLY_SUCCESSFUL = "onlySuccessful",
	CONTRACTOR = "contractor",
	CARDS = "cards",
	MAGNIFYING_GLASS = "magnifyingGlass",

	TYPES = "types",
	STATUSES = "statuses",

	FROM_OR_TO = "fromOrTo",
	TRANSACTION_NUMBER_INPUT = "transactionNumberInput",

	CALLSIGN = "callsign",
	PARK_NUMBER = "parkNumber",

	UPDATE = "update",
	PAYMENT_TYPE_NAME = "paymentTypeName",
	PAYMENT_METHOD = "paymentMethod",

	BALANCE_CHANGE_LOWER = "balanceChangeLower",
	UPDATE_LOWER = "updateLower",
	DATE_LOWER = "dateLower",
	VIEW_ENTRY_LOWER = "viewEntryLower",
	DRIVER_SUMMARY_LOWER = "driverSummaryLower",

	EDIT_CARD = "editCard",
	DELETE_CARD = "deleteCard",
	UPDATE_CARD = "updateCard",
	SHOW_ACTIVE_CARDS = "showActiveCards",
	SHOW_INACTIVE_CARDS = "showInactiveCards",

	BALANCE_CHANGE_SETTLEMENT = "balanceChangeSettlement",
	UPDATE_SETTLEMENT = "updateSettlement",
	DATE_SETTLEMENT = "dateSettlement",

	// BALANCE_CHANGE = "balanceChange",
	CALCULATE_FOR_DAY = "calculateForDay",
	ASSIGN_FINE = "assignFine",
	DRIVER_WORK_HISTORY = "driverWorkHistory",

	SHOW_ACTIVE = "showActive",
	SHOW_INACTIVE = "showInactive",

	//
	// MAIN = "main", // ! executor modal tab
	// USER_SELECT = "userSelect",
	// EXECUTOR_SELECT = "executorSelect",
	// CAR_SELECT = "carSelect",
	// HISTORY = "history",
	OPTIONS = "options", // ! executor modal tab
	ADDITIONAL_DATA = "additionalData", // ! executor modal tab
	CARS = "cars", // ! executor modal tab
	FILES = "files", // ! executor modal tab
	COMMENTS = "comments", // ! executor modal tab
	// ORDERS = "orders", // ! executor modal tab
	// TRANSACTIONS = "transactions", // ! executor modal tab
	// CUSTOMERS = "customers",
	// SCHEDULES = "schedules",
	// PASSPORT = "passport",
	// ACCESS = "access",
	// AVERAGE_SPEED = "averageSpeed",
	// PERMISSION_SELECT = "permissionSelect",
	// AUTOMATIC_FINES = "automaticFines",
	// MANUAL_FINES = "manualFines",
	// REWARD = "reward",

	RETURN = "return",

	PERFORMER_PHOTO = "performerPhoto",
	OTHER_FILES = "otherFiles",
	PASSPORT = "passport",
	TAX_ID = "taxId",
	DRIVER_LICENSE = "driverLicense",
	SOLE_PROPRIETOR = "soleProprietor",
	LICENSE = "license",

	SELECT_CAR = "selectCar",
	DETACH_CAR = "detachCar",

	PERSONAL_DATA = "personalData",
	ACCOUNT = "account",
	WORK_DATA = "workData",
	AVAILABLE_PAYMENT_METHODS = "availablePaymentMethods",
	MAIN_DATA = "mainData",
	PERMISSIONS = "permissions",
	BANK_CARD = "bankCard",

	NOTE = "note",

	LAST_NAME = "lastName",
	FIRST_NAME = "firstName",
	PATRONYMIC = "patronymic",
	WORK_PHONE = "workPhone",
	// LOGIN = "login",
	PASSWORD = "password",
	TIN = "tin",
	PRIORITY = "priority",

	COMPANY = "company",
	BRANCH = "branch",
	CALL_SIGN = "callSign",
	POSITIONS = "positions",
	RESPONSIBLE_EMPLOYEE = "responsibleEmployee",
	EXECUTOR_PENALTY = "executorPenalty",
	SHIFT_SCHEDULE = "shiftSchedule",
	PERFORMER_TEAM = "performerTeam",
	PERFORMER_TARIFF = "performerTariff",

	TECH_PASSPORT = "techPassport",
	INSURANCES = "insurances",
	LICENSE_CARD = "licenseCard",
	MAIN_DOCUMENTS = "mainDocuments",
	MAIN_INSURANCES = "mainInsurances",
	TRANSPORT_CLASSES_DISTRIBUTION = "transportClassesDistribution",
	TRANSPORT_CLASSES_BROADCAST = "transportClassesBroadcast",
	//
	PARKING_NUMBER = "parkingNumber",
	COLOR = "color",
	RELEASE_YEAR = "releaseYear",
	SEATS_COUNT = "seatsCount",
	BODY_NUMBER = "bodyNumber",
	TRANSPORT_TYPE = "transportType",
	TRANSPORT_BRAND = "transportBrand",
	TRANSPORT_MODEL = "transportModel",
	BODY_TYPE = "bodyType",
	TRANSPORT_CLASS = "transportClass",
	TRANSPORT_OWNERSHIP = "transportOwnership",

	ORDER_EXTENDED_FILTER = "orderExtendedFilter",

	REMOUNT_TRANSACTION_TYPE = "remountTransactionType",
	TRANSACTION_TYPE = "transactionType",
}

export default AccessKey;
